import type { Nullable } from '@zen/utils/typescript';

const CELL_HIGHLIGHTED_CLASS = '!bg-grey-lightest';
const MERGE_COLUMN_CELL_CLASS = 'merge-column-cell';
const TABLE_ROW_SELECTOR = 'tr.zen-table-row';

const getCellRowSpan = (element: Nullable<HTMLTableCellElement>): number => {
  const rowSpanAttribute = element?.getAttribute('rowspan');

  return rowSpanAttribute ? parseInt(rowSpanAttribute, 10) : 1;
};

const isMergedCell = (element: Nullable<HTMLTableCellElement>): boolean => {
  return element?.classList.contains(MERGE_COLUMN_CELL_CLASS) ?? false;
};

/**
 * Highlights cells in the specified row range.
 * Regular cells are always highlighted.
 * Merged cells are only highlighted when includeMergedCells is true
 * and their span doesn't extend beyond endRow.
 */
const highlightCellsInRows = (table: Element, startRow: number, endRow: number, includeMergedCells: boolean): void => {
  const rows = table.querySelectorAll<HTMLTableRowElement>(TABLE_ROW_SELECTOR);

  for (let index = startRow; index <= endRow; index++) {
    const row = rows[index];

    if (!row) {
      continue;
    }

    row.querySelectorAll<HTMLTableCellElement>('td').forEach((cell) => {
      const isMerged = cell.classList.contains(MERGE_COLUMN_CELL_CLASS);

      if (isMerged && (!includeMergedCells || index + getCellRowSpan(cell) - 1 > endRow)) {
        return;
      }

      cell.classList.add(CELL_HIGHLIGHTED_CLASS);
    });
  }
};

const removeAllHighlights = (tableId: string): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);

  if (!table) {
    return;
  }

  table
    .querySelectorAll<HTMLTableCellElement>(`.${CSS.escape(CELL_HIGHLIGHTED_CLASS)}`)
    .forEach((cell) => cell.classList.remove(CELL_HIGHLIGHTED_CLASS));
};

export interface CellHoverProps {
  element: Nullable<HTMLTableCellElement>;
  rowIndex: number;
  tableId: string;
}

/**
 * Highlights cells when hovering:
 * - Regular cell: highlights only regular cells in that row
 * - Merged cell: highlights regular cells + fitting merged cells across its row span
 */
export const onCellMouseEnter = ({ element, rowIndex, tableId }: CellHoverProps): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);

  if (!table) {
    return;
  }

  const endRow = rowIndex + getCellRowSpan(element) - 1;

  highlightCellsInRows(table, rowIndex, endRow, isMergedCell(element));
};

export const onCellMouseLeave = ({ tableId }: Pick<CellHoverProps, 'tableId'>): void => {
  removeAllHighlights(tableId);
};
